/** @jsxImportSource ../i18n */
import React, { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchConversations,
  fetchConversation,
  sendMessage,
  deleteConversation,
  clearCurrentConversation,
  clearChatError,
} from '../store/slices/chatSlice';
import { fetchSettings } from '../store/slices/authSlice';
import {
  AlertCircle,
  ArrowLeftRight,
  Ban,
  Eye,
  EyeOff,
  FileCheck,
  FileX,
  History,
  ListMinus,
  ListPlus,
  KeyRound,
  Loader2,
  MessageCircle,
  PanelRightClose,
  Phone,
  Plus,
  Power,
  Send,
  Sparkles,
  Trash2,
  User,
  Users,
  X,
  Zap,
} from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { actionDoneToast, dismissChatAction, runChatAction } from '../services/chatActions';
import { cx } from '../theme/cx';
import { formatDate, formatTime } from '../theme/format';
import { MarkdownProse } from './markdown/MarkdownProse';
import VoiceCall from './voice/VoiceCall';
import api from '../services/api';
import { primeSpeech, voiceSupport } from '../services/voice';
import { Badge, Banner, Button, Checkbox, EmptyState, IconButton, IconTile, Spinner, trapFocus } from './ui';
import { EnvBadge } from './trading/EnvBadge';

const Mascotte = lazy(() => import('./Mascotte'));
const MascotteSpine = lazy(() => import('./MascotteSpine'));

const SPINE_VARIANTS = new Set(['gladys-manga']);
const LG_QUERY = '(min-width: 1024px)';

const SUGGESTIONS = [
  'Analyse le risque de mon portefeuille',
  'Mon PEA est-il bien diversifié ?',
  'Passe en revue mes opérations clôturées',
  'Suggère des ETF éligibles PEA à ajouter',
  'Que devrais-je vendre ou alléger ?',
];

// Gold halo the mascot stands in (desktop pane and mobile strip).
const HALO = 'bg-[radial-gradient(closest-side,rgb(var(--c-gold-500)/0.2),rgb(var(--c-gold-500)/0.06)_55%,transparent)]';

const ACTION_ICONS = {
  watchlist_add: ListPlus, watchlist_remove: ListMinus, analysis_exclude: FileX, analysis_include: FileCheck, debate_start: Users,
  order_ticket: ArrowLeftRight, trading_pause: Power, order_modify: Zap, order_cancel: Ban,
};
// Trading actions are drawn in the Trading universe and say what the button does.
const TRADING_ACTIONS = {
  order_ticket: () => 'Ouvrir le ticket',
  trading_pause: () => 'Mettre en pause',
  order_modify: (a) => (a.change?.order_type === 'market' ? 'Passer au marché' : 'Modifier l’ordre'),
  order_cancel: (a) => ((a.targets || []).length > 1 ? 'Annuler les ordres' : 'Annuler l’ordre'),
};
// What a pending trading action will do, said before the user confirms it.
const TRADING_NOTES = {
  order_ticket: () => 'Rien n’est envoyé : le ticket s’ouvre pré-rempli, vérifié par les garde-fous, et c’est vous qui l’envoyez.',
  order_modify: (a) => (a.change?.order_type === 'market'
    ? 'Un ordre au marché s’exécute aussitôt, au meilleur prix disponible. Les garde-fous sont revérifiés avant la modification.'
    : 'Les garde-fous et Saxo vérifient la modification avant qu’elle ne s’applique.'),
  order_cancel: () => 'Un ordre en carnet est retiré chez Saxo ; un ordre programmé ou à confirmer ne partira pas.',
};
const ACTION_STATES = { done: ['gain', 'Fait'], dismissed: ['neutral', 'Ignorée'], failed: ['loss', 'Échec'] };

// An action Gladys proposed: nothing happens until the user confirms it.
const ActionCard = ({ action, messageId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [liveOk, setLiveOk] = useState(false);
  const saved = typeof messageId === 'number';
  const pending = action.status === 'pending';
  const [tone, label] = ACTION_STATES[action.status] || [];
  const tradingLabel = TRADING_ACTIONS[action.type]?.(action);
  const note = pending && TRADING_NOTES[action.type]?.(action);
  const targets = action.targets || [];
  // Changing a real-money order needs its own tick, as in the order ticket.
  const needsLiveTick = action.type === 'order_modify' && action.env === 'live';

  const confirm = async () => {
    setBusy(true);
    setError('');
    const result = await runChatAction(dispatch, messageId, action, { confirmLive: liveOk });
    setBusy(false);
    if (result.ok) {
      toast.success(actionDoneToast(action));
      if (result.navigateTo) navigate(result.navigateTo);
    } else setError(result.error);
  };

  return (
    <div data-testid="chat-action" className={cx('mt-3 rounded-control border p-3', tradingLabel && 'universe-trading', pending ? 'border-gold-500/35 bg-surface-1' : 'border-line bg-surface-1/60')}>
      <div className="flex items-start gap-2.5">
        <IconTile icon={ACTION_ICONS[action.type] || ListPlus} size="sm" tone={pending ? 'gold' : 'neutral'} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-fg">{action.label}</p>
          {action.description && <p className="truncate text-xs text-fg-subtle">{action.description}</p>}
          {targets.length > 1 && (
            <ul data-testid="chat-action-orders" className="num mt-1.5 space-y-0.5 text-xs text-fg-muted">
              {targets.map((t) => <li key={t.id}>{t.summary}</li>)}
            </ul>
          )}
          {action.question && <p className="mt-1 text-xs italic text-fg-muted">« {action.question} »</p>}
          {action.reason && <p className="mt-1 text-xs italic text-fg-muted">« {action.reason} »</p>}
          {note && <p className="mt-1 text-xs text-fg-subtle">{note}</p>}
        </div>
        {pending && action.env && <EnvBadge env={action.env} className="flex-shrink-0" />}
        {!pending && tone && <Badge tone={tone} dot>{label}</Badge>}
      </div>
      {pending && needsLiveTick && (
        <label className="mt-3 flex items-start gap-2.5 rounded-control border border-loss-500/30 bg-loss-900/40 p-2.5 text-xs text-loss-100">
          <Checkbox checked={liveOk} onChange={(e) => setLiveOk(e.target.checked)} className="mt-px" />
          Je confirme cette modification sur mon compte réel.
        </label>
      )}
      {pending && (
        <div className="mt-3 flex items-center justify-end gap-2">
          {!saved && <span className="mr-auto text-xs text-fg-subtle">Confirmable à la fin de la réponse</span>}
          <Button size="sm" variant="ghost" disabled={!saved || busy} onClick={() => dismissChatAction(dispatch, messageId, action)}>Ignorer</Button>
          <Button size="sm" disabled={!saved || (needsLiveTick && !liveOk)} loading={busy} onClick={confirm}>{tradingLabel || 'Confirmer'}</Button>
        </div>
      )}
      {error && <p role="alert" className="mt-2 text-xs text-loss-300">{error}</p>}
    </div>
  );
};

const Avatar = ({ role }) => (
  <span
    aria-hidden="true"
    className={cx(
      'mt-0.5 inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border',
      role === 'user' ? 'border-gold-500/30 bg-gold-900/60 text-gold-300' : 'border-line bg-surface-2 text-gold-400',
    )}
  >
    {role === 'user' ? <User size={13} /> : <Sparkles size={13} />}
  </span>
);

const Chatbot = ({ isOpen, onClose, initialMessage, mascotVariant, ambient }) => {
  const dispatch = useDispatch();
  const toast = useToast();
  const { conversations, currentConversation, loading, sending, error } = useSelector((state) => state.chat);
  const { settings } = useSelector((state) => state.auth);

  const [message, setMessage] = useState('');
  const [showSidebar, setShowSidebar] = useState(false);
  const [visible, setVisible] = useState(false);
  const [mascotState, setMascotState] = useState('idle');
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const [mobileMascot, setMobileMascot] = useState(() => localStorage.getItem('chat.mobileMascot') !== 'off');
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia(LG_QUERY).matches);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [serverSpeech, setServerSpeech] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const isSpine = SPINE_VARIANTS.has(mascotVariant);
  const mascotOverrideRef = useRef(null);
  const mascotTimer = useRef(null);
  const mascotScrollTimer = useRef(null);
  const messagesRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const textareaRef = useRef(null);
  const panelRef = useRef(null);

  // Animate in/out
  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => setVisible(true));
      dispatch(fetchConversations());
      dispatch(fetchSettings());
      setMascotState('wave');
      if (mascotTimer.current) clearTimeout(mascotTimer.current);
      mascotTimer.current = setTimeout(
        () => setMascotState((cur) => (cur === 'wave' ? 'idle' : cur)),
        2400
      );
    } else {
      setVisible(false);
      setShowSidebar(false);
      setVoiceOpen(false);
      setMascotState('idle');
    }
  }, [dispatch, isOpen]);

  // Voice calls need the server's speech-to-text and a capable browser.
  useEffect(() => {
    if (!isOpen || !voiceSupport()) return undefined;
    let alive = true;
    Promise.resolve(api.get('/voice/config'))
      .then((res) => {
        if (!alive) return;
        setVoiceEnabled(res?.data?.enabled === true);
        setServerSpeech(res?.data?.speech === true);
      })
      .catch(() => { if (alive) setVoiceEnabled(false); });
    return () => { alive = false; };
  }, [isOpen]);

  const onVoicePhase = useCallback((phase) => {
    setMascotState(phase === 'speaking' ? 'talk' : phase === 'thinking' || phase === 'transcribing' ? 'think' : phase === 'listening' ? 'hover' : 'idle');
  }, []);

  // Mobile: shrink Gladys to a head-and-shoulders strip while the on-screen keyboard is up
  useEffect(() => {
    const vv = window.visualViewport;
    if (!isOpen || !vv) return undefined;
    const onResize = () => setKeyboardOpen(vv.height < window.innerHeight * 0.75);
    onResize();
    vv.addEventListener('resize', onResize);
    return () => vv.removeEventListener('resize', onResize);
  }, [isOpen]);

  useEffect(() => {
    const mq = window.matchMedia(LG_QUERY);
    const onChange = (e) => setIsDesktop(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const toggleMobileMascot = () => {
    setMobileMascot((on) => {
      localStorage.setItem('chat.mobileMascot', on ? 'off' : 'on');
      return !on;
    });
  };

  // Set initial message when provided
  useEffect(() => {
    if (isOpen && initialMessage) {
      setMessage(initialMessage);
      // Start a new conversation when injecting context
      dispatch(clearCurrentConversation());
    }
  }, [dispatch, isOpen, initialMessage]);

  const lastMessage = currentConversation?.messages?.[currentConversation.messages.length - 1];
  const streaming = sending && lastMessage?.streaming === true;

  // Follow new content, but while a reply streams in only if the reader is
  // already near the bottom, so scrolling up to reread isn't yanked back.
  const stickToBottom = useRef(true);
  useEffect(() => {
    if (streaming && !stickToBottom.current) return;
    messagesEndRef.current?.scrollIntoView({ behavior: streaming ? 'auto' : 'smooth' });
  }, [currentConversation?.messages, sending, streaming]);

  // Gladys: quick nod on send, then think while waiting for the API
  useEffect(() => {
    if (!isOpen) return;
    if (sending) {
      setMascotState('nod');
      const id = setTimeout(
        () => setMascotState((cur) => (cur === 'nod' ? 'think' : cur)),
        900
      );
      return () => clearTimeout(id);
    }
  }, [isOpen, sending]);

  const [waitSeconds, setWaitSeconds] = useState(0);
  useEffect(() => {
    if (!sending) return undefined;
    setWaitSeconds(0);
    const started = Date.now();
    const id = setInterval(() => setWaitSeconds(Math.floor((Date.now() - started) / 1000)), 1000);
    return () => clearInterval(id);
  }, [sending]);

  // Gladys talks while the reply is being written, smiles once it's complete
  useEffect(() => {
    if (!isOpen || !streaming) return;
    if (mascotTimer.current) clearTimeout(mascotTimer.current);
    setMascotState('talk');
  }, [isOpen, streaming]);

  const wasSending = useRef(false);
  useEffect(() => {
    if (wasSending.current && !sending) {
      const ok = lastMessage?.role === 'assistant';
      setMascotState(ok ? 'smile' : 'idle');
      if (mascotTimer.current) clearTimeout(mascotTimer.current);
      if (ok) mascotTimer.current = setTimeout(() => setMascotState((cur) => (cur === 'smile' ? 'idle' : cur)), 1800);
    }
    wasSending.current = sending;
  }, [sending, lastMessage]);

  // User interactions on the mascot briefly override the chat-driven state
  const onMascotInteract = useCallback((s) => {
    mascotOverrideRef.current = s;
    if (s === 'click' || s === 'hover') {
      setMascotState(s);
      if (mascotTimer.current) clearTimeout(mascotTimer.current);
      mascotTimer.current = setTimeout(() => {
        mascotOverrideRef.current = null;
        setMascotState((cur) => (cur === s ? 'idle' : cur));
      }, s === 'click' ? 1500 : 4000);
    } else if (s === 'idle') {
      mascotOverrideRef.current = null;
      setMascotState((cur) => (cur === 'hover' ? 'idle' : cur));
    }
  }, []);

  useEffect(() => {
    if (isOpen && !sending) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [isOpen, sending, currentConversation]);

  // Auto-resize textarea
  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = '40px';
      el.style.height = Math.min(el.scrollHeight, 120) + 'px';
    }
  }, []);

  useEffect(() => {
    autoResize();
  }, [message, autoResize]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!message.trim() || sending) return;

    const msg = message;
    setMessage('');
    setShowSidebar(false);
    stickToBottom.current = true;
    const result = await dispatch(sendMessage({
      conversationId: currentConversation?.id || 0,
      message: msg,
    }));
    if (sendMessage.rejected.match(result)) setMessage((cur) => cur || msg);
  };

  const handleNewConversation = () => {
    dispatch(clearCurrentConversation());
    setShowSidebar(false);
    setMessage('');
  };

  const handleSelectConversation = (conv) => {
    dispatch(fetchConversation(conv.id));
    setShowSidebar(false);
  };

  const handleDeleteConversation = async (convId) => {
    const result = await dispatch(deleteConversation(convId));
    if (deleteConversation.fulfilled.match(result)) toast.success('Conversation supprimée');
  };

  const handleClose = useCallback(() => {
    setVisible(false);
    setTimeout(onClose, 200);
  }, [onClose]);

  // Keyboard: Escape closes the history first, then the panel; Tab stays
  // inside the panel; focus returns to the opener once closed.
  const showSidebarRef = useRef(showSidebar);
  showSidebarRef.current = showSidebar;
  useEffect(() => {
    if (!isOpen) return undefined;
    const opener = document.activeElement;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (showSidebarRef.current) setShowSidebar(false);
        else handleClose();
      } else if (event.key === 'Tab' && panelRef.current) {
        trapFocus(event, panelRef.current);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (opener && document.contains(opener)) opener.focus?.();
    };
  }, [isOpen, handleClose]);

  const hasApiKey = settings?.has_api_key === true;
  const messages = currentConversation?.messages || [];
  const hasContext = initialMessage && message === initialMessage;
  const contextPending = hasContext && messages.length === 0;

  // At rest, Gladys keeps the ambient mood of the market.
  const shownState = mascotState === 'idle' && ambient ? ambient.mascot : mascotState;

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop — full screen; left half (desktop) hosts Gladys in a gold halo */}
      <div
        className={`fixed inset-0 z-50 transition-opacity duration-200 ${visible ? 'opacity-100' : 'opacity-0'}`}
      >
        <div className="absolute inset-0 bg-black/50" onClick={handleClose} />
        <div
          className="absolute inset-y-0 left-0 right-1/2 hidden items-end justify-center bg-canvas/60 backdrop-blur-sm lg:flex"
          onClick={handleClose}
        >
          <div aria-hidden="true" className={cx('pointer-events-none absolute inset-x-0 bottom-0 mx-auto h-[560px] w-[680px] max-w-full', HALO)} />
          <div className="relative h-[620px] w-[400px]" onClick={(e) => e.stopPropagation()}>
            <Suspense fallback={null}>
              {isSpine ? (
                isDesktop && <MascotteSpine state={shownState} onInteract={onMascotInteract} />
              ) : (
                <Mascotte key={mascotVariant || 'gladys'} state={shownState} onInteract={onMascotInteract} variant={mascotVariant} />
              )}
            </Suspense>
          </div>
        </div>
      </div>

      {/* Slide-in panel — right half on desktop */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Gladys"
        className={`fixed bottom-0 right-0 top-0 z-50 flex w-full flex-col border-l border-line bg-surface-1 shadow-pop
                    transition-transform duration-200 ease-out sm:w-[480px] lg:w-1/2
                    ${visible ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
          <div className="flex min-w-0 items-center gap-3">
            <IconTile icon={Sparkles} size="sm" />
            <div className="min-w-0">
              <h2 className="font-display text-lg leading-tight text-fg">Gladys</h2>
              <p className="max-w-[260px] truncate text-xs text-fg-subtle">
                {currentConversation?.title || ambient?.detail || 'Votre assistante patrimoniale'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-0.5">
            {isSpine && (
              <IconButton
                icon={mobileMascot ? EyeOff : Eye}
                label={mobileMascot ? 'Masquer Gladys' : 'Afficher Gladys'}
                size="sm"
                tooltipSide="bottom-end"
                className="lg:hidden"
                data-focus-skip={isDesktop || undefined}
                onClick={toggleMobileMascot}
              />
            )}
            {voiceEnabled && hasApiKey && (
              <IconButton icon={Phone} label="Appel vocal avec Gladys" size="sm" tooltipSide="bottom-end" pressed={voiceOpen} onClick={() => { primeSpeech(); setShowSidebar(false); setVoiceOpen((v) => !v); }} />
            )}
            <IconButton icon={Plus} label="Nouvelle conversation" size="sm" tooltipSide="bottom-end" onClick={handleNewConversation} />
            <IconButton icon={History} label="Historique des conversations" size="sm" tooltipSide="bottom-end" pressed={showSidebar} onClick={() => setShowSidebar(!showSidebar)} />
            <IconButton icon={X} label="Fermer" size="sm" tooltipSide="bottom-end" onClick={handleClose} />
          </div>
        </div>

        {/* Mobile / tablet: Gladys framed head-to-waist above the conversation */}
        {isSpine && mobileMascot && !isDesktop && (
          <div
            className={`relative flex-shrink-0 overflow-hidden border-b border-line bg-canvas/60 transition-[height] duration-200 lg:hidden
                        ${keyboardOpen ? 'h-24' : 'h-[32dvh] min-h-[170px] max-h-[300px]'}`}
          >
            <div aria-hidden="true" className={cx('pointer-events-none absolute inset-x-0 -bottom-1/3 mx-auto h-full w-[420px] max-w-full', HALO)} />
            <Suspense fallback={null}>
              <MascotteSpine state={shownState} onInteract={onMascotInteract} framing="bust" />
            </Suspense>
          </div>
        )}

        {/* Body */}
        <div className="relative flex flex-1 overflow-hidden">
          {voiceOpen && <VoiceCall onClose={() => setVoiceOpen(false)} mascotVariant={mascotVariant} onPhase={onVoicePhase} serverSpeech={serverSpeech} />}
          {/* Conversation history overlay */}
          {showSidebar && (
            <div className="absolute inset-0 z-10 overflow-y-auto bg-surface-1/95 backdrop-blur-sm">
              <div className="space-y-1 p-3">
                <div className="mb-2 flex items-center justify-between pl-1">
                  <p className="eyebrow">Conversations</p>
                  <IconButton icon={PanelRightClose} label="Fermer l’historique" size="sm" tooltipSide="bottom-end" onClick={() => setShowSidebar(false)} />
                </div>
                <button
                  type="button"
                  onClick={handleNewConversation}
                  className="flex w-full items-center gap-2 rounded-control border border-dashed border-line-strong p-2.5 text-left text-sm text-fg-muted transition-colors hover:border-gold-500/40 hover:bg-surface-2 hover:text-fg"
                >
                  <Plus size={16} aria-hidden="true" />
                  Nouvelle conversation
                </button>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Spinner size={20} label="Chargement des conversations…" />
                  </div>
                ) : conversations.length === 0 ? (
                  <p className="py-8 text-center text-sm text-fg-subtle">Aucune conversation pour le moment</p>
                ) : (
                  conversations.map((conv) => {
                    const current = currentConversation?.id === conv.id;
                    return (
                      <div
                        key={conv.id}
                        className={cx(
                          'flex items-center gap-1 rounded-control border pr-1 transition-colors',
                          current ? 'border-gold-500/25 bg-gold-950/40' : 'border-transparent hover:bg-surface-2',
                        )}
                      >
                        <button
                          type="button"
                          onClick={() => handleSelectConversation(conv)}
                          aria-current={current || undefined}
                          className="flex min-w-0 flex-1 items-center gap-2.5 rounded-control p-2.5 text-left"
                        >
                          <MessageCircle size={14} className={cx('flex-shrink-0', current ? 'text-gold-400' : 'text-fg-subtle')} aria-hidden="true" />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm text-fg">{conv.title}</span>
                            <span className="block text-[11px] text-fg-subtle">{formatDate(conv.updated_at)}</span>
                          </span>
                        </button>
                        <IconButton
                          icon={Trash2}
                          label={`Supprimer « ${conv.title} »`}
                          variant="danger"
                          size="sm"
                          tooltip={false}
                          onClick={() => handleDeleteConversation(conv.id)}
                        />
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {/* Messages */}
          <div
            className="flex-1 overflow-y-auto"
            ref={messagesRef}
            onScroll={(e) => {
              const el = e.currentTarget;
              stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
              const v = el.scrollTop - (el._lastST || 0);
              el._lastST = el.scrollTop;
              window.dispatchEvent(new CustomEvent('mascotte:scroll', { detail: v / 8 }));
              if (!mascotOverrideRef.current && Math.abs(v) > 12 && mascotState !== 'talk' && mascotState !== 'think') {
                setMascotState('scroll');
                if (mascotScrollTimer.current) clearTimeout(mascotScrollTimer.current);
                mascotScrollTimer.current = setTimeout(() => {
                  setMascotState((cur) => (cur === 'scroll' ? 'idle' : cur));
                }, 700);
              }
            }}
          >
            {!settings ? (
              <div className="flex h-full items-center justify-center">
                <Spinner size={20} label="Chargement…" />
              </div>
            ) : !hasApiKey ? (
              <div className="flex h-full items-center justify-center">
                <EmptyState
                  icon={KeyRound}
                  tone="warn"
                  title="Clé API requise"
                  description="Configurez votre clé OpenAI ou Gemini dans les réglages pour discuter avec Gladys."
                  action={<Button as={Link} to="/settings" variant="secondary" size="sm" onClick={handleClose}>Ouvrir les réglages</Button>}
                />
              </div>
            ) : messages.length === 0 && !hasContext ? (
              <div className="flex min-h-full flex-col items-center justify-center px-6 py-8 text-center">
                <IconTile icon={Sparkles} size="lg" />
                <h3 className="mt-4 font-display text-2xl text-fg">Bonjour, je suis Gladys</h3>
                <p className="mt-2 max-w-sm text-sm leading-relaxed text-fg-muted">
                  Votre portefeuille et l’historique de vos opérations sont joints automatiquement à chaque question.
                </p>
                <div className="mt-6 flex w-full max-w-sm flex-col gap-2">
                  {SUGGESTIONS.map((text) => (
                    <button
                      key={text}
                      type="button"
                      onClick={() => {
                        setMessage(text);
                        setTimeout(() => inputRef.current?.focus(), 50);
                      }}
                      className="rounded-control border border-line bg-surface-2/50 px-3.5 py-2.5 text-left text-sm text-fg-body transition-colors duration-150 hover:border-gold-500/40 hover:bg-surface-2 hover:text-fg"
                    >
                      {text}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-4 p-4">
                {/* Context banner if initial message is from analysis */}
                {contextPending && (
                  <Banner
                    tone="gold"
                    compact
                    title="Contexte d’analyse chargé"
                    action={<IconButton icon={X} label="Retirer le contexte" size="sm" tooltip={false} onClick={() => setMessage('')} />}
                  >
                    Appuyez sur Entrée pour l’envoyer, ou précisez d’abord votre question.
                  </Banner>
                )}

                {messages.map((msg, idx) => {
                  const user = msg.role === 'user';
                  return (
                    <div key={msg.id || idx} className={cx('flex gap-2.5', user && 'flex-row-reverse')}>
                      <Avatar role={msg.role} />
                      <div
                        className={cx(
                          'min-w-0 max-w-[85%] rounded-card px-3.5 py-2.5',
                          user ? 'rounded-tr-md border border-gold-500/25 bg-gold-900/60' : 'rounded-tl-md border border-line bg-surface-2',
                        )}
                      >
                        {user ? (
                          <p className="whitespace-pre-wrap break-words text-[13.5px] leading-relaxed text-fg">
                            {msg.content.length > 500
                              ? msg.content.substring(0, 200) + '… [contexte tronqué]'
                              : msg.content}
                          </p>
                        ) : (
                          <>
                            <MarkdownProse variant="chat">{msg.content}</MarkdownProse>
                            {msg.streaming && (
                              <span className="ml-0.5 inline-block h-3.5 w-1.5 animate-pulse rounded-sm bg-gold-400 align-middle" aria-hidden="true" />
                            )}
                            {(msg.actions || []).map((action) => <ActionCard key={action.id} action={action} messageId={msg.id} />)}
                          </>
                        )}
                        {msg.created_at && !msg.streaming && (
                          <p className={cx('num mt-1 text-[11px] text-fg-subtle', user ? 'text-right' : 'text-left')}>
                            {formatTime(msg.created_at)}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}

                {sending && !streaming && (
                  <div className="flex gap-2.5">
                    <Avatar role="assistant" />
                    <div className="rounded-card rounded-tl-md border border-line bg-surface-2 px-3.5 py-3" role="status" aria-live="polite">
                      <div className="flex items-center gap-1.5">
                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gold-400" style={{ animationDelay: '0ms' }} />
                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gold-400" style={{ animationDelay: '150ms' }} />
                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-gold-400" style={{ animationDelay: '300ms' }} />
                        <span className="ml-1 text-xs text-fg-muted">
                          Gladys réfléchit…{waitSeconds >= 3 && <span className="num text-fg-subtle"> {waitSeconds} s</span>}
                        </span>
                      </div>
                      {waitSeconds >= 15 && (
                        <p className="mt-1.5 text-[11px] text-fg-subtle">Une réponse détaillée sur votre portefeuille peut prendre jusqu’à une minute.</p>
                      )}
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div role="alert" className="flex items-center gap-2 border-t border-loss-500/25 bg-loss-900/40 py-1.5 pl-4 pr-2">
            <AlertCircle size={14} className="flex-shrink-0 text-loss-400" aria-hidden="true" />
            <span className="flex-1 truncate text-xs text-loss-200" title={error}>{error}</span>
            <IconButton icon={X} label="Masquer l’erreur" size="sm" tooltip={false} onClick={() => dispatch(clearChatError())} />
          </div>
        )}

        {/* Input (hidden during a voice call) */}
        {hasApiKey && !voiceOpen && (
          <form onSubmit={handleSend} className="border-t border-line bg-surface-1 p-3">
            {contextPending && (
              <p className="mb-2 truncate px-1 text-xs text-fg-subtle">
                Contexte : {initialMessage.substring(0, 80)}…
              </p>
            )}
            <div className="flex items-end gap-2">
              <textarea
                ref={(el) => { inputRef.current = el; textareaRef.current = el; }}
                value={contextPending ? '' : message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend(e);
                  }
                }}
                aria-label="Message à Gladys"
                placeholder={contextPending ? 'Entrée pour envoyer le contexte, ou posez une question précise…' : 'Posez une question sur votre portefeuille…'}
                disabled={sending}
                rows={1}
                className="flex-1 resize-none rounded-control border border-line-strong bg-surface-2 px-3.5 py-2 text-sm leading-6 text-fg transition-colors duration-150
                           placeholder:text-fg-subtle hover:border-fg-faint focus:border-gold-500/60 focus:outline-none focus:ring-2 focus:ring-gold-500/20
                           disabled:opacity-50"
                style={{ minHeight: '40px', maxHeight: '120px' }}
              />
              <IconButton
                type="submit"
                variant="primary"
                icon={sending ? Loader2 : Send}
                iconClassName={sending ? 'animate-spin' : undefined}
                label="Envoyer"
                tooltipSide="top-end"
                disabled={(!message.trim() && !hasContext) || sending}
              />
            </div>
          </form>
        )}
      </div>
    </>
  );
};

export default Chatbot;
